import { models } from 'powerbi-client';
import type { Report, VisualDescriptor } from 'powerbi-client';
import type { IFilter } from 'powerbi-models';
import { MetadataError } from '@ep/error-handling';

import { log } from '../../../util/log';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { generateUuidV4 } from '../../../util/uuid';

const SOURCE = 'lib/app/powerbi/report-visual-capture.ts';

const NON_DATA_VISUAL_TYPES = new Set(['actionButton', 'shape', 'image', 'bookmarkNavigator']);

/** Bounds pathological cells (the observed SVG-embedded-slicer artifact chief among them). */
const CELL_MAX_CHARS = 200;

/** One visual's exported data — the raw PowerBI identifier */
export interface CapturedVisual {
  id: string;
  title: string;
  type: string;
  columns: string[];
  rows: string[][];
}

/** One active slicer's selection, in the same shape `IFilter` carries. */
export interface CapturedSlicerSelection {
  target: string;
  operator: string;
  values: string[];
}

export interface ReportVisualCaptureResult {
  status: 'ok' | 'unavailable';
  pageName?: string;
  pageDisplayName?: string;
  visuals?: CapturedVisual[];
  slicerSelections?: CapturedSlicerSelection[];
  failedVisualTitles?: string[];
}

export interface ReportFilterCaptureResult {
  pageName: string;
  slicerSelections: CapturedSlicerSelection[];
}

export interface NamedVisualCaptureResult {
  visuals: CapturedVisual[];
  missingIds: string[];
}

function isVisibleVisual(visual: VisualDescriptor): boolean {
  return visual.layout?.displayState?.mode !== models.VisualContainerDisplayMode.Hidden;
}

/** A substring match, not an enum of known types — PowerBI's slicer type strings vary
 * (`listSlicer`, `advancedSlicerVisual` observed) and this is not meant to be exhaustive. */
function isSlicerVisual(visual: VisualDescriptor): boolean {
  return visual.type.toLowerCase().includes('slicer');
}

function truncateCell(value: string): string {
  return value.length > CELL_MAX_CHARS ? value.slice(0, CELL_MAX_CHARS) : value;
}

export function logReportCaptureFailure(
  functionName: string,
  reason: unknown,
  metadata: Record<string, unknown>
): void {
  const trackingId: string = generateUuidV4();
  log(
    'error',
    new MetadataError(
      reason instanceof Error ? reason.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, functionName),
      metadata,
      trackingId
    )
  );
}

/**
 * Parses raw CSV text into rows of unescaped field values, respecting RFC 4180 double-quote
 * escaping across the *entire* text, not line by line. A quoted field can legitimately contain a
 * literal newline — observed in a real capture as an inline SVG data URI in a custom slicer's
 * export. Splitting on `\n` before parsing quotes shreds that one field into dozens of fake rows
 * and desyncs quote-tracking at every fragment boundary — which is also what would let an
 * untruncated blob slip past the per-cell cap.
 */
function parseVisualDataCsv(text: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let insideQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (insideQuotes) {
      if (char === '"' && text[index + 1] === '"') {
        currentField += '"';
        index += 1;
      } else if (char === '"') {
        insideQuotes = false;
      } else {
        currentField += char;
      }
      continue;
    }

    if (char === '"') {
      insideQuotes = true;
    } else if (char === ',') {
      currentRow.push(currentField);
      currentField = '';
    } else if (char === '\r') {
      // Swallowed; a following '\n' (or a lone '\r') ends the row below.
    } else if (char === '\n') {
      currentRow.push(currentField);
      rows.push(currentRow);
      currentRow = [];
      currentField = '';
    } else {
      currentField += char;
    }
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  return rows.filter((row) => row.length > 1 || row[0] !== '');
}

function toCapturedSlicerSelection(filter: IFilter): CapturedSlicerSelection {
  const rawFilter = filter as unknown as {
    target?: unknown;
    operator?: string;
    values?: unknown[];
  };
  return {
    target: truncateCell(JSON.stringify(rawFilter.target ?? {})),
    operator: truncateCell(rawFilter.operator ?? 'Unknown'),
    values: Array.isArray(rawFilter.values)
      ? rawFilter.values.map((value) => truncateCell(String(value)))
      : []
  };
}

async function captureSlicerSelection(
  visual: VisualDescriptor
): Promise<CapturedSlicerSelection[]> {
  try {
    const slicerState = await visual.getSlicerState();
    return slicerState.filters
      .filter((filter) => (filter as unknown as { operator?: string }).operator !== 'All')
      .map((filter) => toCapturedSlicerSelection(filter as unknown as IFilter));
  } catch (err: unknown) {
    logReportCaptureFailure('captureSlicerSelection', err, {
      visualId: visual.name,
      visualType: visual.type
    });
    return [];
  }
}

interface CaptureVisualResult {
  visual: CapturedVisual;
  slicerSelections: CapturedSlicerSelection[];
}

async function captureVisual(
  visual: VisualDescriptor,
  rows?: number
): Promise<CaptureVisualResult> {
  const [visualExport, slicerSelections] = await Promise.all([
    visual.exportData(models.ExportDataType.Summarized, rows),
    isSlicerVisual(visual)
      ? captureSlicerSelection(visual)
      : Promise.resolve<CapturedSlicerSelection[]>([])
  ]);

  const parsedVisualData = parseVisualDataCsv(visualExport.data);
  const [headerRow, ...bodyRows] = parsedVisualData;

  return {
    visual: {
      id: visual.name,
      title: truncateCell(visual.title || 'Untitled visual'),
      type: visual.type,
      columns: (headerRow ?? []).map(truncateCell),
      rows: bodyRows.map((row) => row.map(truncateCell))
    },
    slicerSelections
  };
}

/** This capture runs on every grounded question (not once per report), to describe the page as it
 * stands now — so it exports one row per visual rather than the full, uncapped export: only
 * `columns` is read from it (see `toManifestVisual`), never a row count or a value. The true row
 * count is learned later, as `rowsTotal` on the data a `requestVisualData` call returns. */
const MANIFEST_CAPTURE_ROW_LIMIT = 1;

/**
 * Captures every data-bearing visual's schema from `report`'s active page, plus
 * active slicer selections and the page name.
 */
export async function captureReportVisuals(report: Report): Promise<ReportVisualCaptureResult> {
  const activePage = await report.getActivePage();
  const allVisuals = await activePage.getVisuals();

  const visibleVisuals = allVisuals.filter(isVisibleVisual);
  const dataBearingVisuals = visibleVisuals.filter(
    (visual) => !NON_DATA_VISUAL_TYPES.has(visual.type)
  );

  if (dataBearingVisuals.length === 0) {
    return { status: 'unavailable' };
  }

  const settledVisualResults = await Promise.allSettled(
    dataBearingVisuals.map((visual) => captureVisual(visual, MANIFEST_CAPTURE_ROW_LIMIT))
  );

  const capturedVisualResults: CaptureVisualResult[] = [];
  const failedVisualTitles: string[] = [];

  settledVisualResults.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      capturedVisualResults.push(result.value);
      return;
    }
    const visual = dataBearingVisuals[index];
    failedVisualTitles.push(truncateCell(visual.title || 'Untitled visual'));
    logReportCaptureFailure('captureReportVisuals', result.reason, {
      visualId: visual.name,
      visualType: visual.type
    });
  });

  if (capturedVisualResults.length === 0) {
    return { status: 'unavailable' };
  }

  return {
    status: 'ok',
    pageName: activePage.name,
    pageDisplayName: truncateCell(activePage.displayName),
    visuals: capturedVisualResults.map((result) => result.visual),
    slicerSelections: capturedVisualResults.flatMap((result) => result.slicerSelections),
    ...(failedVisualTitles.length > 0 ? { failedVisualTitles } : {})
  };
}

export async function captureReportFilters(report: Report): Promise<ReportFilterCaptureResult> {
  const activePage = await report.getActivePage();
  const allVisuals = await activePage.getVisuals();

  const settledSlicerSelections = await Promise.allSettled(
    allVisuals.filter(isSlicerVisual).map((visual) => captureSlicerSelection(visual))
  );

  return {
    pageName: activePage.name,
    slicerSelections: settledSlicerSelections.flatMap((result) =>
      result.status === 'fulfilled' ? result.value : []
    )
  };
}

export async function captureNamedVisuals(
  report: Report,
  visualIds: readonly string[]
): Promise<NamedVisualCaptureResult> {
  const activePage = await report.getActivePage();
  const allVisuals = await activePage.getVisuals();

  const visualsById = new Map(allVisuals.map((visual) => [visual.name, visual]));
  const requestedVisuals = visualIds
    .map((visualId) => visualsById.get(visualId))
    .filter((visual): visual is VisualDescriptor => visual !== undefined);

  const settledVisualResults = await Promise.allSettled(
    requestedVisuals.map((visual) => captureVisual(visual))
  );

  const visuals: CapturedVisual[] = [];

  settledVisualResults.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      visuals.push(result.value.visual);
      return;
    }
    const visual = requestedVisuals[index];
    logReportCaptureFailure('captureNamedVisuals', result.reason, {
      visualId: visual.name,
      visualType: visual.type
    });
  });

  const capturedIds = new Set(visuals.map((visual) => visual.id));

  return {
    visuals,
    missingIds: visualIds.filter((visualId) => !capturedIds.has(visualId))
  };
}
